import { Flex, Skeleton } from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { useUserState } from '~/atoms/UserState'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { useClipboard } from '~/hooks/useClipboard'
import { useKeyPresses } from '~/hooks/useKeyboard'
import {
  CallDetailsAnalyticsContent,
  TCallAnalyticsTab,
} from '~/pages/Calls/CallDetails/CallDetailsAnalyticsContent'
import { CallDetailsBasicContent } from '~/pages/Calls/CallDetails/CallDetailsBasicContent'
import { CallDetailsViewHeader } from '~/pages/Calls/CallDetails/CallDetailsViewHeader'
import { adaptMargoSummaryResponse } from '~/pages/Calls/CallDetails/MargoSummary/MargoSummary.adapter'
import { useMargoCallReport } from '~/pages/Calls/CallDetails/MargoSummary/MargoSummary.queries'
import { hasValue } from '~/utils/common'
import { downloadAudio } from '~/utils/downloadaudio'
import { shouldPanelHandleEscape } from '~/utils/keyboardScope'
import { getShareableWebAppUrl } from '~/utils/shareableUrl'

import { CallDirection, CallInstance } from '@mangovoice/types'

export interface TranscriptAudioPlayerState {
  isAudioPlaying: boolean
  playerPosition: number
  requestedTimestamp: number
  onPlayStateChange: (isPlaying: boolean) => void
  onSetPlayerTime: (millis: number) => void
}

interface CallDetailsContentProps {
  callId?: number
  callInstance?: CallInstance
  callInstanceLoading: boolean
  onClosed?: () => void
  isModal: boolean
  onOpenModal?: () => void
  onVoicemailClick?: () => void
  initialTab?: TCallAnalyticsTab
}
export const CallDetailsContent = ({
  callId,
  callInstance,
  callInstanceLoading,
  onClosed,
  isModal,
  onOpenModal,
  initialTab,
}: CallDetailsContentProps) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const playbackTimestampRef = useRef<number>(undefined)
  const { copyToClipboard } = useClipboard()
  const { toastSuccess, toastError } = useToastAlert()

  const { callAnalyticsEnabled } = useUserState({ canAutoRefresh: true })
  const { data: margoCallReport } = useMargoCallReport({
    callId,
    pbxId: callInstance?.pbx.id,
  })

  const [routeTimestamp, setRouteTimestamp] = useState<number>()
  const margoSummary = adaptMargoSummaryResponse(margoCallReport)

  const isEligibleForTranscription = useMemo(() => {
    if (!callAnalyticsEnabled || !callInstance) return false

    // Determine if a transcript exists.
    const transcriptReady =
      Boolean(callInstance.transcript_id) ||
      callInstance.transcription_status?.startsWith('COMPLETE')

    // If a transcript exists, we consider the call eligible even if it's missed.
    if (transcriptReady) return true

    // Otherwise, require that the call is not missed and meets the other criteria.
    return (
      callAnalyticsEnabled &&
      callInstance &&
      !callInstance.is_missed &&
      callInstance.direction !== CallDirection.Internal &&
      callInstance?.transcription_status !== 'AUDIO_TOO_SHORT' &&
      callInstance?.transcription_status !== 'AUDIO_TOO_LONG'
    )
  }, [callAnalyticsEnabled, callInstance])

  const handleCopyLink = (withTimeStamp = false) => {
    copyToClipboard(
      getShareableWebAppUrl(`/calls/${callId}`, {
        t: withTimeStamp ? playbackTimestampRef.current : undefined,
      }),
      () => toastSuccess(t('callDetails.toast.linkCopied')),
      () => toastError(t('callDetails.toast.linkCopyFailed')),
    )
  }

  const desiredRouteTimestamp = useMemo(() => {
    return searchParams.get('t') ? Number(searchParams.get('t')) : undefined
  }, [searchParams.get('t')])

  useEffect(() => {
    setRouteTimestamp(
      desiredRouteTimestamp ? Number(desiredRouteTimestamp) : undefined,
    )
  }, [callId, desiredRouteTimestamp])

  //sets up open/close animations cleanly
  const shouldBeClosed = !hasValue(callId)
  useEffect(() => {
    //when selected call id changes, always clear the playback timestamp
    playbackTimestampRef.current = undefined
  }, [callId])

  useKeyPresses({
    filter: shouldPanelHandleEscape,
    onPress: (_) => {
      onClosed?.()
    },
  })

  const handleDownloadRecording = () => {
    if (callInstance?.recording_url) {
      downloadAudio(callInstance.recording_url, `${callInstance?.id}.mp3`)
    }
  }

  // useEffect(() => {
  //   if (initialTimestamp) {
  //     playbackTimestampRef.current = initialTimestamp
  //   }
  // }, [initialTimestamp])
  return (
    <Flex
      flex={isModal ? 'none' : '1'}
      flexDirection="column"
      bg="bg.default"
      width="full"
    >
      <CallDetailsViewHeader
        callInstanceLoading={callInstanceLoading}
        callInstance={callInstance}
        shouldBeClosed={shouldBeClosed}
        onClosed={onClosed}
        isModal={isModal}
        handleCopyLink={handleCopyLink}
        onOpenModal={onOpenModal}
        onDownloadAudio={handleDownloadRecording}
      />
      {callInstanceLoading ? (
        <Flex p="1.5rem" w="full" flexDir="column" gap="1.25rem">
          <Skeleton h="5.625rem" />
          <Flex w="full" h="9.625rem" gap="1.25rem">
            <Skeleton flexGrow="1" />
            <Skeleton flexGrow="1" />
          </Flex>
          <Skeleton h="10.375rem" />
          <Skeleton h="7.75rem" />
        </Flex>
      ) : isEligibleForTranscription && callInstance ? (
        <CallDetailsAnalyticsContent
          callInstance={callInstance}
          margoSummary={margoSummary}
          isModal={isModal}
          initialTab={initialTab}
          initialTimestamp={desiredRouteTimestamp}
          onRequestedTimestampChange={setRouteTimestamp}
          playbackTimestampRef={playbackTimestampRef}
        />
      ) : (
        callInstance && (
          <CallDetailsBasicContent
            callInstance={callInstance}
            margoSummary={margoSummary}
            isModal={isModal}
            requestedTimestamp={routeTimestamp}
            playbackTimestampRef={playbackTimestampRef}
          />
        )
      )}
    </Flex>
  )
}
